import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import {
  themes,
  brightColorTimePicker,
  defaultAllSelection,
} from "services/constants";
import { showToast } from "services/utils/Status";
import dayjs from "dayjs";
import sessionHandling from "services/utils/notificationUtils";
import { customGetAPI } from "services/utils/CustomAPI";
import { DatePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import {
  AddCircleOutline,
  Close,
  ArrowUpward,
  ArrowDownward,
  SwapVert,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { entryMethod } from "pages/TeamZone/TeamZoneHeaders";
import ShiftDetailsRow from "../ShiftDetailsRow";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors } from "services/constants/designTokens";
import { PunchSection, PunchSectionShell, punchRowKey } from "../PunchEntries";

// Punch dates reach this screen as DD-MM-YYYY (API) or YYYY-MM-DD (after the
// date picker changes one), so both helpers accept either.
const toYMD = (date) => {
  if (!date || typeof date !== "string") return date;
  const parts = date.split("-");
  return parts[0].length === 4 ? date : parts.reverse().join("-");
};
const toDMY = (date) => {
  if (!date || typeof date !== "string") return date;
  const parts = date.split("-");
  return parts[0].length === 4 ? parts.reverse().join("-") : date;
};
// Chronological sort key; rows still missing a time sink to the bottom.
const punchStamp = (entry) => `${toYMD(entry?.date) || "9999"}T${entry?.time || "99:99"}`;
export const PunchDetails = ({
  shiftDetails,
  attendanceDetails,
  setUpdatePunchEntry,
  updatePunchEntry,
  departmentId,
  setGetEntryDetails,
  setAttendanceDetails,
  overallData,
  setGetUpdatedData,
  reCallApi,
  setEditState,
  setRecallApi,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token } = useSelector((state) => state.tokenReducer);

  // Seeded from props so the rows are there on the first paint. Starting
  // empty and filling in from the effect below made every open flash
  // "Used Punches (0) / No used punches" before the data appeared.
  const [entriesDetails, setEntries] = useState(() =>
    (attendanceDetails?.punch_entries || []).map((data) => ({
      ...data,
      rowId: data.rowId ? data.rowId : data.punch_id,
    }))
  );
  const [count, setCount] = useState(1);
  const [checked, setChecked] = useState(attendanceDetails.early_exit);
  const [newShiftDetails, setNewShiftDetails] = useState({
    shiftName: shiftDetails?.shift_name,
    shiftid: shiftDetails?.shift_id,
  });
  const [departmentShiftDetails, setDepartmentShiftDetails] = useState([]);
  const [getDepartmentShifts, setGetDepartmentShifts] = useState(true);
  const unusedPunches = attendanceDetails?.unused_punches ?? [];
  // Rows parked in Unused during this edit keep their rowId (so they can be
  // moved back untouched); count them too so a new row never reuses one.
  let countLength = Math.max(
    0,
    ...entriesDetails.map((entry) => Number(entry.rowId) || 0),
    ...unusedPunches.map((row) => Number(row?._entry?.rowId) || 0)
  );

  useEffect(() => {
    const fetchIndividualUserDetails = async () => {
      const url =
        departmentId !== defaultAllSelection
          ? `${domain}deparment-shift-filter/?department_id=${departmentId}&date=${attendanceDetails?.date
              .split("-")
              .reverse()
              .join("-")}&company_id=${requestCompanyId}`
          : `${domain}all/shift-filter/`;
      const res = await customGetAPI(url);
      if (res.ok) {
        setDepartmentShiftDetails(
          res?.responseDetails.filter((data) => {
            return data?.shift_name !== "Weekoff";
          })
        );
      } else {
        setDepartmentShiftDetails([]);
      }
    };
    if (getDepartmentShifts) {
      fetchIndividualUserDetails();
      setGetDepartmentShifts(false);
    }
  }, [
    attendanceDetails?.date,
    departmentId,
    domain,
    getDepartmentShifts,
    shiftDetails.date,
    shiftDetails.shift_filter_date, requestCompanyId
  ]);

  useEffect(() => {
    const updatePunchDetails = () => {
      if (attendanceDetails?.length !== 0) {
        let updatedData = attendanceDetails?.punch_entries?.map((data) => ({
          ...data,
          rowId: data.rowId ? data.rowId : data.punch_id,
        }));
        setEntries(updatedData);
        setChecked(attendanceDetails.early_exit);
        setCount(updatedData.length + 1);
      }
    };
    if (updatePunchEntry) {
      updatePunchDetails();
      setUpdatePunchEntry(false);
    }
  }, [
    attendanceDetails.early_exit,
    attendanceDetails?.length,
    attendanceDetails.make_as_absent,
    attendanceDetails?.punch_entries,
    attendanceDetails?.weekoff_pay,
    setUpdatePunchEntry,
    updatePunchEntry,
  ]);

  const handleEntryLogin = (id, value) => {
    let updatedTime = entriesDetails.map((data) =>
      data.rowId === id
        ? { ...data, direction: value ? value.label : null }
        : data
    );
    setEntries(updatedTime);
    updtaeGlobalInOUTEntries(id, value);
  };

  const updtaeGlobalInOUTEntries = (id, value) => {
    const updatedOverallData = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        const updatedPunchEntries = (attendance_map?.punch_entries || []).map(
          (entry) =>
            entry.rowId === id
              ? {
                  ...entry,
                  direction: value ? value.label : null,
                }
              : entry
        );
        return {
          ...attendance_map,
          punch_entries: updatedPunchEntries,
        };
      }
      return attendance_map;
    });
    setAttendanceDetails(updatedOverallData);
  };

  const addNewCheckInOutEntries = () => {
    if (validateFieldValues()) {
      // Alternate from the latest punch by time, not the last row in the
      // list: a punch brought back from Unused or moved from another day
      // can sit anywhere, and the list order gave IN after IN.
      const latestEntry =
        [...entriesDetails]
          .filter((entry) => entry.time)
          .sort((a, b) => punchStamp(a).localeCompare(punchStamp(b)))
          .pop() ?? entriesDetails[entriesDetails.length - 1];
      let lastEntryDirection = latestEntry ? latestEntry.direction : "OUT";
      let newEntry = {
        rowId: countLength + 1,
        date: attendanceDetails?.date,
        time: null,
        direction: lastEntryDirection === "OUT" ? "IN" : "OUT",
      };
      setEntries([...entriesDetails, newEntry]);
      countLength = countLength + 1;
      const updatedOverallData = overallData.map((attendance_map) => {
        if (attendance_map.id === attendanceDetails.id) {
          return {
            ...attendance_map,
            punch_entries: [...(attendance_map?.punch_entries || []), newEntry],
            error: "",
          };
        }
        return attendance_map;
      });

      setAttendanceDetails(updatedOverallData);
      setCount(count + 1);
    }
  };

  const updateTimeConfigurations = (newTime, id) => {
    let updatedTime = entriesDetails.map((data) =>
      data.rowId === id
        ? { ...data, time: dayjs(newTime).format("HH:mm") }
        : data
    );
    updateGlobalData(newTime, id);
    setEntries(updatedTime);
  };

  const updateGlobalData = (newTime, id) => {
    const updatedOverallData = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        const updatedPunchEntries = (attendance_map?.punch_entries || []).map(
          (entry) =>
            entry.rowId === id
              ? {
                  ...entry,
                  time: newTime ? dayjs(newTime).format("HH:mm") : null,
                }
              : entry
        );
        return {
          ...attendance_map,
          punch_entries: updatedPunchEntries,
        };
      }
      return attendance_map;
    });
    setAttendanceDetails(updatedOverallData);
  };

  // Remove takes the punch out of `punch_entries`, so the PUT on Save leaves
  // it out (the original remove behaviour). A punch brought back from Unused
  // in this edit still exists there on the server, so removing it returns it
  // to Unused instead of making it vanish.
  const removeCheckInOutEntryRow = (rowData) => {
    if (rowData._unusedRow) {
      moveToUnused(rowData);
      return;
    }
    const overAllAttendanceDetails = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        const updatedPunchEntries = (
          attendance_map?.punch_entries || []
        ).filter((entry) => entry.rowId !== rowData.rowId);
        return {
          ...attendance_map,
          punch_entries: updatedPunchEntries,
          early_exit:
            updatedPunchEntries.length === 0
              ? false
              : attendance_map.early_exit,
        };
      }
      return attendance_map;
    });

    applySwap(overAllAttendanceDetails);
  };

  // Used -> Unused. Nothing is sent until Save: the punch simply leaves
  // `punch_entries` (the list the PUT sends) and is shown under Unused so it
  // can be brought back. A punch that came from Unused earlier in this edit
  // returns to its original unused row.
  // The Used rows are drawn from this component's own copy (entriesDetails),
  // while the Unused rows come straight from the parent data. Updating only
  // the parent left the two a frame apart: the punch showed in both sections
  // or in neither (flashing the "No ... punches" text) until the sync effect
  // caught up. Setting both in the same update removes that blink.
  const applySwap = (updatedOverallData) => {
    const current = updatedOverallData.find(
      (attendance_map) => attendance_map.id === attendanceDetails.id
    );
    setEntries(
      (current?.punch_entries || []).map((data) => ({
        ...data,
        rowId: data.rowId ? data.rowId : data.punch_id,
      }))
    );
    setChecked(current?.early_exit);
    setAttendanceDetails(updatedOverallData);
  };

  const moveToUnused = (rowData) => {
    const updatedOverallData = overallData.map((attendance_map) => {
      if (attendance_map.id !== attendanceDetails.id) return attendance_map;
      const updatedPunchEntries = (attendance_map?.punch_entries || []).filter(
        (entry) => entry.rowId !== rowData.rowId
      );
      const usedInfo =
        (attendance_map?.used_punches || []).find(
          (used) => rowData.punch_id && used.punch_id === rowData.punch_id
        ) || {};
      const unusedRow = rowData._unusedRow ?? {
        source_type: usedInfo.source_type ?? null,
        source: usedInfo.source ?? rowData.device_name ?? null,
        global_event_id: usedInfo.global_event_id ?? null,
        global_event_code: usedInfo.global_event_code ?? null,
        record_source: "pending_unused",
        punch_id: rowData.punch_id ?? null,
        void_id: null,
        date: toDMY(rowData.date),
        time: rowData.time,
        direction: rowData.direction,
        _pendingNote: "Unused after Save",
        is_edited: false,
        edit: null,
        _pendingKey: `pending-${rowData.rowId}`,
        _entry: rowData,
      };
      return {
        ...attendance_map,
        punch_entries: updatedPunchEntries,
        unused_punches: [...(attendance_map?.unused_punches || []), unusedRow],
        early_exit:
          updatedPunchEntries.length === 0 ? false : attendance_map.early_exit,
      };
    });
    applySwap(updatedOverallData);
  };

  // Unused -> Used. A punch parked during this edit goes back exactly as it
  // was. A backend unused (void) row is added as a new entry carrying its
  // date/time/direction; it keeps a reference to the void row so it can be
  // returned to Unused again before saving.
  const moveToUsed = (unusedRow) => {
    const key = punchRowKey(unusedRow);
    const updatedOverallData = overallData.map((attendance_map) => {
      if (attendance_map.id !== attendanceDetails.id) return attendance_map;
      const currentEntries = attendance_map?.punch_entries || [];
      const currentUnused = attendance_map?.unused_punches || [];
      const nextRowId =
        Math.max(
          0,
          ...currentEntries.map((entry) => Number(entry.rowId) || 0),
          ...currentUnused.map((row) => Number(row?._entry?.rowId) || 0)
        ) + 1;
      const entry = unusedRow._entry ?? {
        rowId: nextRowId,
        date: unusedRow.date,
        time: unusedRow.time,
        direction: unusedRow.direction,
        _unusedRow: unusedRow,
      };
      return {
        ...attendance_map,
        punch_entries: [...currentEntries, entry].sort((a, b) =>
          punchStamp(a).localeCompare(punchStamp(b))
        ),
        unused_punches: currentUnused.filter(
          (row) => punchRowKey(row) !== key
        ),
        error: "",
      };
    });
    applySwap(updatedOverallData);
  };

  // Rows are matched by rowId: new rows have no punch_id, so matching on
  // punch_id (undefined === undefined) moved or dropped every new row of the
  // day at once. rowIds are only unique within a day, so a moved row that
  // collides with one in the target day gets the next free id there.
  const withFreeRowId = (rowData, targetEntries) => {
    if (!targetEntries.some((entry) => entry.rowId === rowData.rowId)) {
      return rowData;
    }
    const nextRowId =
      Math.max(0, ...targetEntries.map((entry) => Number(entry.rowId) || 0)) + 1;
    return { ...rowData, rowId: nextRowId };
  };

  const arrowUpward = (rowData) => {
    const previousRowDateCheck = overallData.find(
      (item) => item.id === attendanceDetails.id - 1
    );
    if (!previousRowDateCheck) return;
    const parseDMY = (dateStr) => {
      const [day, month, year] = toDMY(dateStr).split("-");
      return new Date(year, month - 1, day);
    };

    const currentDate = parseDMY(rowData.date);
    const prevDate = parseDMY(previousRowDateCheck.date);
    const diffInDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);

    if (!(diffInDays === 0 || diffInDays === 1)) {
      showToast("error", "Cannot move entry. Dates do not match.");
      return;
    }

    let overAllAttendanceDetails = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        return {
          ...attendance_map,
          punch_entries: (attendance_map?.punch_entries || []).filter(
            (entry) => entry.rowId !== rowData.rowId
          ),
        };
      }
      if (attendance_map.id === attendanceDetails.id - 1) {
        const targetEntries = attendance_map?.punch_entries || [];
        return {
          ...attendance_map,
          punch_entries: [
            ...targetEntries,
            withFreeRowId(rowData, targetEntries),
          ],
        };
      } else {
        return attendance_map;
      }
    });
    setAttendanceDetails(overAllAttendanceDetails);
    setUpdatePunchEntry(true);
  };

  const arrowDownWard = (rowData) => {
    const nextRowDateCheck = overallData.find(
      (item) => item.id === attendanceDetails.id + 1
    );
    if (!nextRowDateCheck) return;
    // The date picker stores YYYY-MM-DD while API rows are DD-MM-YYYY, so
    // compare in one format.
    if (toYMD(rowData.date) !== toYMD(nextRowDateCheck.date)) {
      showToast("error", "Cannot move entry. Dates do not match.");
      return;
    }
    let overAllAttendanceDetails = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        return {
          ...attendance_map,
          punch_entries: (attendance_map?.punch_entries || []).filter(
            (entry) => entry.rowId !== rowData.rowId
          ),
        };
      }
      if (attendance_map.id === attendanceDetails.id + 1) {
        const targetEntries = attendance_map?.punch_entries || [];
        return {
          ...attendance_map,
          punch_entries: [
            withFreeRowId(rowData, targetEntries),
            ...targetEntries,
          ],
        };
      } else {
        return attendance_map;
      }
    });
    setAttendanceDetails(overAllAttendanceDetails);
    setUpdatePunchEntry(true);
  };

  const updateEntryDate = (newValue, id) => {
    let formatedData = dayjs(newValue).format("DD-MM-YYYY");
    let updatedTime = entriesDetails.map((data) =>
      data.rowId === id ? { ...data, date: formatedData } : data
    );
    setEntries(updatedTime);
    updateGlobalEntryData(newValue, id);
  };

  const updateGlobalEntryData = (newValue, id) => {
    const updatedOverallData = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        let formatedData = dayjs(newValue).format("YYYY-MM-DD");
        const updatedPunchEntries = (attendance_map?.punch_entries || []).map(
          (entry) =>
            entry.rowId === id
              ? {
                  ...entry,
                  date: formatedData,
                }
              : entry
        );
        return {
          ...attendance_map,
          punch_entries: updatedPunchEntries,
        };
      }
      return attendance_map;
    });
    setAttendanceDetails(updatedOverallData);
  };

  const validateFieldValues = () => {
    const findNullValue = entriesDetails.filter(
      (entry) =>
        !entry.date ||
        !entry.direction ||
        !entry.date ||
        entry.date === "Invalid Date" ||
        entry.time === "Invalid Date" ||
        !entry.time
    );
    if (findNullValue.length <= 0) {
      return true;
    } else {
      let overalldataupdated = overallData.map((attendance_map) => {
        if (attendance_map.id === attendanceDetails.id) {
          return {
            ...attendance_map,
            error: "Value is required",
          };
        }
        return attendance_map;
      });
      setAttendanceDetails(overalldataupdated);
      return false;
    }
  };

  const handleChange = (e, attendanceDetails) => {
    let overalldataupdated = overallData.map((attendance_map) => {
      if (attendance_map.id === attendanceDetails.id) {
        return {
          ...attendance_map,
          early_exit: e.target.checked,
        };
      }
      return attendance_map;
    });
    setAttendanceDetails(overalldataupdated);
    setChecked(e.target.checked);
  };

  const handleShiftName = (data) => {
    if (data) {
      setNewShiftDetails({
        shiftName: data?.shift_name,
        shiftid: data?.shift_id,
      });
    }
  };

  const updateShiftBasedOnRange = async () => {
    try {
      const response = await fetch(`${domain}shift_assignments/daily/?company_id=${requestCompanyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          shift_assignments: [
            {
              user: shiftDetails?.user_id,
              daily_assignments: [
                {
                  shift_id: newShiftDetails?.shiftid,
                  date: attendanceDetails?.date.split("-").reverse().join("-"),
                  is_week_off: newShiftDetails?.shiftName === "Weekoff",
                },
              ],
            },
          ],
        }),
      });
      const res = await response.json();
      if (response.ok) {
        showToast("success", "Shift changed.");
        setAttendanceDetails([]);
        setGetEntryDetails(true);
        setGetUpdatedData(true);
        // A shift change is a real change: refresh the details panel and
        // let the list behind it know it needs a reload.
        setRecallApi?.(true);
        setEditState?.(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error('REQUEST_FAILED');
      }
    } catch (error) {
      console.error('[Punchdetails] operation failed:', error);
      showToast("error", "Couldn't save the attendance entry. Please try again.");
    }
  };

  // Each punch row is its own grid, so an "auto" icon column made the rows
  // with the extra previous/next-day arrow narrower than the rest. The icon
  // column gets one fixed width per card instead: the most icons any row in
  // this card shows, and at least three on a multi-day edit so cards line up
  // with each other too. Conditions mirror the icons rendered below.
  const showsPrevDayArrow = attendanceDetails?.id !== 1;
  const showsNextDayArrow = overallData?.length !== attendanceDetails?.id;
  const rowIconCount = (row, index) =>
    1 +
    (row?.punch_id || row?._unusedRow ? 1 : 0) +
    (showsPrevDayArrow && index === 0 ? 1 : 0) +
    (showsNextDayArrow && index === (entriesDetails?.length ?? 0) - 1 ? 1 : 0);
  const actionIconSlots = Math.max(
    2,
    (overallData?.length ?? 0) > 1 ? 3 : 0,
    ...(entriesDetails || []).map(rowIconCount),
  );
  // Small IconButton: 24px icon + 5px padding each side.
  const actionColumnWidth = `${actionIconSlots * 34}px`;

  return (
    <>
      <React.Fragment>
        <Mui.Grid
          container
          sx={{
            paddingLeft: 2,
            paddingRight: 2,
            paddingTop: 0.5,
            paddingBottom: 2,
          }}
        >
          <Mui.Grid
            container
            sx={{
              border: `2px solid ${themes.sidebarDivider}`,
              borderRadius: "10px 10px 10px 10px",
              backgroundColor: themes.whiteColor,
            }}
          >
            <Mui.Grid container>
              <ShiftDetailsRow
                attendanceDetails={attendanceDetails}
                departmentShiftDetails={departmentShiftDetails}
                handleShiftName={handleShiftName}
                updateShiftBasedOnRange={updateShiftBasedOnRange}
                themes={themes}
                showAutoComplete={true}
              />
            </Mui.Grid>
            <Mui.Grid
              container
              sx={{
                borderBottom: `2px solid ${themes.sidebarDivider}`,
              }}
            ></Mui.Grid>

            <Mui.Grid item xs={12} sx={{ p: 1.5, pb: 0 }}>
            <PunchSectionShell
              tone="used"
              title="Used Punches"
              count={entriesDetails?.length ?? 0}
            >
            {entriesDetails?.length === 0 ? (
              <Mui.Typography
                sx={{
                  p: 2,
                  textAlign: "center",
                  fontSize: 14,
                  color: designColors.slate600,
                }}
              >
                No used punches
              </Mui.Typography>
            ) : null}
            {entriesDetails?.map((data, index) => (
              <Mui.Box
                key={data?.rowId ?? index}
                // Sized to the drawer, not the viewport: the old lg/xl Grid
                // fractions gave a wider date than time field and squeezed
                // the icons against the card edge in the narrow panel.
                sx={{
                  display: "grid",
                  // The date needs the most room (DD/MM/YYYY plus the
                  // calendar icon); with the swap/remove/day-move icons
                  // taking space, equal columns clipped its year. Time and
                  // direction give up width first.
                  gridTemplateColumns: {
                    xs: `minmax(0, 1fr) ${actionColumnWidth}`,
                    sm: `minmax(150px, 1.3fr) minmax(110px, 1fr) minmax(90px, 0.9fr) ${actionColumnWidth}`,
                  },
                  columnGap: 1,
                  alignItems: "start",
                  px: 1,
                  py: 0.5,
                }}
              >
                <Mui.Box
                  sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", sm: "auto" } }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker"]}>
                      <div style={{ width: "100%" }}>
                        <DatePicker
                          size="small"
                          slotProps={{
                            textField: {
                              size: "small",
                              inputProps: {
                                readOnly: true,
                                onKeyDown: (e) => {
                                  if (
                                    e.key === "ArrowUp" ||
                                    e.key === "ArrowDown"
                                  ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }
                                },
                              },
                            },
                          }}
                          sx={{ width: "100%" }}
                          value={dayjs(data?.date, "DD/MM/YYYY")}
                          minDate={dayjs(attendanceDetails?.date, "DD/MM/YYYY")}
                          maxDate={
                            dayjs(attendanceDetails?.date, "DD/MM/YYYY").isSame(
                              dayjs(),
                              "day"
                            )
                              ? dayjs()
                              : dayjs(
                                  attendanceDetails?.date,
                                  "DD/MM/YYYY"
                                ).add(1, "day")
                          }
                          onChange={(newValue) => {
                            updateEntryDate(newValue, data.rowId);
                          }}
                          format="DD/MM/YYYY"
                        />
                      </div>
                    </DemoContainer>
                  </LocalizationProvider>
                  {(data?.date === "Invalid Date" || !data?.date) &&
                  attendanceDetails?.error === "Value is required" ? (
                    <Mui.Typography
                      variant="body2"
                      color="error"
                      sx={{ fontSize: 13, paddingLeft: 1, paddingTop: 0.5 }}
                    >
                      Date is required
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
                <Mui.Box
                  sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", sm: "auto" } }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["TimePicker"]}>
                      <div style={{ width: "100%" }}>
                        <TimePicker
                          ampm={false}
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          value={data?.time ? dayjs(data.time, "HH:mm") : null}
                          slotProps={brightColorTimePicker}
                          sx={{ width: "100%" }}
                          onChange={(newValue) => {
                            updateTimeConfigurations(newValue, data.rowId);
                          }}
                        />
                      </div>
                    </DemoContainer>
                  </LocalizationProvider>
                  {(data?.time === "Invalid Date" || !data?.time) &&
                  attendanceDetails?.error === "Value is required" ? (
                    <Mui.Typography
                      variant="body2"
                      color="error"
                      sx={{ fontSize: 13, paddingLeft: 1, paddingTop: 0.5 }}
                    >
                      Time is required
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
                {/* pt matches the DemoContainer's top padding so all three
                    fields line up. */}
                <Mui.Box sx={{ pt: 1, minWidth: 0 }}>
                  <Mui.Autocomplete
                    id="combo-box-demo"
                    size="small"
                    value={data?.direction}
                    options={entryMethod}
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.label
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    renderOption={(props, option) => {
                      return <li {...props}>{option.label}</li>;
                    }}
                    onChange={(event, value) => {
                      handleEntryLogin(data?.rowId, value);
                    }}
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <Mui.TextField
                        {...params}
                        value={data?.direction}
                        sx={{ background: themes.whiteColor }}
                        placeholder={"Select entry type"}
                        size="small"
                      />
                    )}
                  />
                  {!data?.direction &&
                  attendanceDetails?.error === "Value is required" ? (
                    <Mui.Typography
                      variant="body2"
                      color="error"
                      sx={{ fontSize: 13, paddingLeft: 1, paddingTop: 0.5 }}
                    >
                      Direction is required
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
                <Mui.Box sx={{ pt: 1, display: "flex", alignItems: "center" }}>
                  {data?.punch_id || data?._unusedRow ? (
                    <Mui.Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title="Move to Unused Punches"
                    >
                      <Mui.IconButton
                        size="small"
                        aria-label="Move to Unused Punches"
                        onClick={() => {
                          moveToUnused(data);
                        }}
                      >
                        <SwapVert />
                      </Mui.IconButton>
                    </Mui.Tooltip>
                  ) : null}
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title={
                      data?.punch_id || data?._unusedRow
                        ? "Remove entry"
                        : "Remove new entry"
                    }
                  >
                    <Mui.IconButton
                      size="small"
                      aria-label={
                        data?.punch_id || data?._unusedRow
                          ? "Remove entry"
                          : "Remove new entry"
                      }
                      onClick={() => {
                        removeCheckInOutEntryRow(data);
                      }}
                    >
                      <Close />
                    </Mui.IconButton>
                  </Mui.Tooltip>
                  {attendanceDetails?.id === 1 ||
                    (index === 0 && (
                      <Mui.IconButton
                        size="small"
                        aria-label="Move to previous day"
                        onClick={() => {
                          arrowUpward(data);
                        }}
                      >
                        <ArrowUpward />
                      </Mui.IconButton>
                    ))}
                  {overallData?.length === attendanceDetails?.id ||
                    (index === entriesDetails?.length - 1 && (
                      <Mui.IconButton
                        size="small"
                        aria-label="Move to next day"
                        onClick={() => {
                          arrowDownWard(data);
                        }}
                      >
                        <ArrowDownward />
                      </Mui.IconButton>
                    ))}
                </Mui.Box>
              </Mui.Box>
            ))}

            <Mui.Grid container sx={{ padding: 1 }}>
              <Mui.Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="Add punch entry"
              >
                <Mui.IconButton
                  aria-label="Add punch entry"
                  onClick={() => {
                    addNewCheckInOutEntries();
                  }}
                >
                  <AddCircleOutline />
                </Mui.IconButton>
              </Mui.Tooltip>
            </Mui.Grid>
            </PunchSectionShell>
            <PunchSection
              tone="unused"
              title="Unused Punches"
              rows={unusedPunches}
              recordDate={toDMY(attendanceDetails?.date)}
              editsOpenByDefault={false}
              renderAction={(row) => (
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Move to Used Punches"
                >
                  <Mui.IconButton
                    aria-label="Move to Used Punches"
                    onClick={() => {
                      moveToUsed(row);
                    }}
                  >
                    <SwapVert />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )}
            />
            </Mui.Grid>
            <Mui.Grid
              container
              sx={{
                borderBottom: `2px solid ${themes.sidebarDivider}`,
              }}
            ></Mui.Grid>
            <Mui.Grid
              container
              sx={{ padding: 1, paddingLeft: 1 }}
              alignItems="center"
            >
              <Mui.Grid item xs={0.5}>
                <Mui.Checkbox
                  checked={checked}
                  onChange={(e) => handleChange(e, attendanceDetails)}
                  inputProps={{ "aria-label": "controlled" }}
                  disabled={attendanceDetails?.punch_entries.length === 0}
                />
              </Mui.Grid>

              <Mui.Grid item xs={0.3}></Mui.Grid>

              <Mui.Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="If the Early Exit flag is clicked, the break entries go to check-out, and the check-in without check-out entries go missing."
              >
                <Mui.Grid item>
                  <Mui.Typography
                    sx={{
                      fontSize: 16,
                      color: themes.headLine,
                      fontWeight: 600,
                    }}
                  >
                    Early Exit
                  </Mui.Typography>
                </Mui.Grid>
              </Mui.Tooltip>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </React.Fragment>
    </>
  );
};
